// routes/ForgotPassword.tsx
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useForgotPassword } from "./api";
import { toast } from "react-toastify";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

const schema = z.object({ email: z.string().email("Enter a valid email") });

export default function ForgotPassword() {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(schema),
  });

  const forgotPasswordMutation = useForgotPassword();
  const [serverError, setServerError] = useState("");

  const onSubmit = (data: { email: string }) => {
    if (!navigator.onLine) {
      toast.error("No internet connection. Please check your network.");
      return;
    }
    forgotPasswordMutation.mutate(data.email, {
      onSuccess: () => {
        toast.success("If that email exists, we’ve sent a reset link.", {
          position: "top-center",
          autoClose: 5000,
          hideProgressBar: true,
          theme: "colored",
        });
      },
      onError: (error: any) => {
        setServerError(
          error.response?.data?.message ||
            "An error occurred while sending the reset link"
        );
      },
    });
  };

  useEffect(() => {
    if (serverError) {
      toast.error(serverError);
    }
  }, [serverError]);

  return (
    <div className='max-w-sm mx-auto p-6 border-solid border-2 border-[#D9D9D9] w-3/4 rounded-md mt-10'>
      <form onSubmit={handleSubmit(onSubmit)} className='space-y-3'>
        <label htmlFor='forgot-email' className='block mb-1'>
          Enter email to send password reset link
        </label>
        <input
          id='forgot-email'
          type='email'
          placeholder='Email'
          autoFocus
          {...register("email")}
          className='border rounded w-full px-2 py-1 outline-none'
          aria-invalid={!!errors.email}
          aria-describedby={errors.email ? "forgot-email-error" : undefined}
        />
        {errors.email && (
          <p className='text-red-500 text-sm'>{errors.email.message}</p>
        )}
        <button
          type='submit'
          disabled={forgotPasswordMutation.isPending}
          className='bg-[#0275D8] text-white rounded-md w-full py-2 hover:bg-[#0256A1] disabled:bg-gray-400 mt-5'
        >
          {forgotPasswordMutation.isPending ? "Sending…" : "Send reset link"}
        </button>
      </form>
      <div className='mt-4 text-center'>
        <Link to='/log-in' className='underline'>
          Back to login
        </Link>
      </div>
    </div>
  );
}
